import { Component, Input, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Chart, registerables } from 'chart.js';
import { forkJoin } from 'rxjs';
import { switchMap, finalize } from 'rxjs/operators';
import { ApiEstimativaService } from 'src/app/modules/shared/services/api-estimativa.service';
import { DataService } from 'src/app/modules/shared/services/data.service';
import { LanguageService } from 'src/app/modules/shared/services/language.service';

interface DeviationRow {
    sectionType: string;
    sectionName: string;
    estimatedHours: number;
    actualHours: number;
    deviation: number;
    deviationPercent: number;
}

interface SectionSummary {
    name: string;
    estimated: number;
    actual: number;
    deviation: number;
    deviationPercent: number;
}

interface Conclusion {
    icon: string;
    text: string;
}

@Component({
  standalone: false,
  selector: 'app-analisis-desviaciones',
    templateUrl: './analisis-desviaciones.component.html',
    styleUrls: ['./analisis-desviaciones.component.css']
})
export class AnalisisDesviacionesComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() sideNavStatus!: boolean;

    variables: any = {};
    deviationRows: DeviationRow[] = [];
    sectionSummaries: SectionSummary[] = [];
    topDeviationRows: DeviationRow[] = [];
    conclusions: Conclusion[] = [];
    isLoading: boolean = true;
    hasData: boolean = false;
    isExporting: boolean = false;

    totalEstimated: number = 0;
    totalActual: number = 0;
    totalDeviation: number = 0;
    totalDeviationPercent: number = 0;

    private barChart: Chart<'bar', number[], string> | undefined;
    private pieChart: Chart<'doughnut', number[], string> | undefined;
    private chartsReady: boolean = false;

    // Colors matching the mockup
    private readonly PURPLE = '#7c4dff';
    private readonly ORANGE = '#ff6d00';
    private readonly PURPLE_LIGHT = 'rgba(124, 77, 255, 0.8)';
    private readonly ORANGE_LIGHT = 'rgba(255, 109, 0, 0.8)';

    constructor(
        private dataService: DataService,
        private estimativaService: ApiEstimativaService,
        private languageService: LanguageService
    ) {
        Chart.register(...registerables);
    }

    ngOnInit(): void {
        this.languageService.translations$.subscribe((vars: any) => {
            this.variables = vars;
        });

        this.loadData(this.resolveEstimateId());
    }

    ngAfterViewInit(): void {
        this.chartsReady = true;
    }

    ngOnDestroy(): void {
        if (this.barChart) { this.barChart.destroy(); }
        if (this.pieChart) { this.pieChart.destroy(); }
    }

    private resolveEstimateId(): string {
        const estimate = this.dataService.getEstimate();
        return typeof estimate === 'object' ? this.dataService.getEstimateId() : estimate;
    }

    private loadData(estimateId: string): void {
        this.isLoading = true;

        this.estimativaService.getImputation(estimateId).subscribe({
            next: (imputation: any) => {
                if (imputation && imputation.imputations && imputation.imputations.length > 0) {
                    this.hasData = true;
                    this.buildDeviationTable(imputation.imputations);
                    this.buildSectionSummaries();
                    this.calculateTotals();
                    this.buildTopDeviations();
                    this.buildConclusions();
                    this.isLoading = false;

                    // Charts need a tick to render after *ngIf="hasData" shows the canvases
                    setTimeout(() => this.initCharts(), 50);
                } else {
                    this.hasData = false;
                    this.isLoading = false;
                }
            },
            error: (err: any) => {
                console.error('[AnalisisDesviaciones] Error:', err);
                this.hasData = false;
                this.isLoading = false;
            }
        });
    }

    private buildDeviationTable(imputations: any[]): void {
        this.deviationRows = imputations.map(item => {
            const estimated = item.estimatedHours || 0;
            const actual = item.actualHours || 0;
            const deviation = actual - estimated;
            const deviationPercent = estimated > 0 ? ((deviation / estimated) * 100) : 0;

            return {
                sectionType: item.sectionType,
                sectionName: item.sectionName,
                estimatedHours: estimated,
                actualHours: actual,
                deviation: deviation,
                deviationPercent: Math.round(deviationPercent * 10) / 10
            };
        });
    }

    private buildSectionSummaries(): void {
        const groups = new Map<string, { estimated: number, actual: number }>();
        for (const row of this.deviationRows) {
            if (!groups.has(row.sectionType)) {
                groups.set(row.sectionType, { estimated: 0, actual: 0 });
            }
            const g = groups.get(row.sectionType)!;
            g.estimated += row.estimatedHours;
            g.actual += row.actualHours;
        }

        this.sectionSummaries = [];
        groups.forEach((val, name) => {
            const deviation = val.actual - val.estimated;
            const deviationPercent = val.estimated > 0 ? Math.round(((deviation / val.estimated) * 100) * 10) / 10 : 0;
            this.sectionSummaries.push({
                name,
                estimated: val.estimated,
                actual: val.actual,
                deviation,
                deviationPercent
            });
        });
    }

    private calculateTotals(): void {
        this.totalEstimated = this.deviationRows.reduce((sum, r) => sum + r.estimatedHours, 0);
        this.totalActual = this.deviationRows.reduce((sum, r) => sum + r.actualHours, 0);
        this.totalDeviation = this.totalActual - this.totalEstimated;
        this.totalDeviationPercent = this.totalEstimated > 0
            ? Math.round(((this.totalDeviation / this.totalEstimated) * 100) * 10) / 10
            : 0;
    }

    private buildTopDeviations(): void {
        // Show top 5 tasks sorted by absolute deviation percentage (exclude zero deviations)
        this.topDeviationRows = [...this.deviationRows]
            .filter(r => Math.abs(r.deviationPercent) > 0)
            .sort((a, b) => Math.abs(b.deviationPercent) - Math.abs(a.deviationPercent))
            .slice(0, 5);
    }

    private buildConclusions(): void {
        this.conclusions = [];
        const diff = Math.abs(this.totalDeviation);

        if (this.totalDeviation > 0) {
            this.conclusions.push({
                icon: '📈',
                text: (this.variables.conclusionOverBudget || 'El proyecto ha consumido {0} horas más de las estimadas.')
                    .replace('{0}', diff.toFixed(1))
            });
        } else if (this.totalDeviation < 0) {
            this.conclusions.push({
                icon: '📉',
                text: (this.variables.conclusionUnderBudget || 'El proyecto ha consumido {0} horas menos de las estimadas.')
                    .replace('{0}', diff.toFixed(1))
            });
        } else {
            this.conclusions.push({
                icon: '✅',
                text: this.variables.conclusionOnBudget || 'El proyecto se ha completado exactamente según la estimación.'
            });
        }

        this.conclusions.push({
            icon: '💡',
            text: this.variables.conclusionRecommendation || 'Se recomienda revisar el proceso de estimación y considerar estos datos para proyectos futuros.'
        });
    }

    // --- Charts ---
    private initCharts(): void {
        this.initBarChart();
        this.initPieChart();
    }

    private initBarChart(): void {
        const canvas = document.getElementById('barChart') as HTMLCanvasElement;
        if (!canvas) return;

        const labels = this.sectionSummaries.map(s => s.name);
        const estimatedData = this.sectionSummaries.map(s => s.estimated);
        const actualData = this.sectionSummaries.map(s => s.actual);

        this.barChart = new Chart(canvas, {
            type: 'bar',
            data: {
                labels: labels,
                datasets: [
                    {
                        label: this.variables.estimatedHoursLabel || 'Horas Estimadas',
                        data: estimatedData,
                        backgroundColor: this.PURPLE_LIGHT,
                        borderColor: this.PURPLE,
                        borderWidth: 1,
                        borderRadius: 4
                    },
                    {
                        label: this.variables.actualHoursLabel || 'Horas Reales',
                        data: actualData,
                        backgroundColor: this.ORANGE_LIGHT,
                        borderColor: this.ORANGE,
                        borderWidth: 1,
                        borderRadius: 4
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'bottom',
                        labels: {
                            usePointStyle: true,
                            padding: 20,
                            font: { size: 12, family: "'Segoe UI', 'Raleway'" }
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: this.variables.hours || 'Horas',
                            font: { size: 12 }
                        },
                        grid: { color: 'rgba(0,0,0,0.05)' }
                    },
                    x: {
                        grid: { display: false },
                        ticks: {
                            maxRotation: 45,
                            minRotation: 20,
                            font: { size: 11 }
                        }
                    }
                }
            }
        });
    }

    private initPieChart(): void {
        const canvas = document.getElementById('pieChart') as HTMLCanvasElement;
        if (!canvas) return;

        this.pieChart = new Chart(canvas, {
            type: 'doughnut',
            data: {
                labels: [
                    (this.variables.estimatedHoursLabel || 'Horas Estimadas') + ': ' + this.totalEstimated.toFixed(0) + 'h',
                    (this.variables.actualHoursLabel || 'Horas Reales') + ': ' + this.totalActual.toFixed(0) + 'h'
                ],
                datasets: [{
                    data: [this.totalEstimated, this.totalActual],
                    backgroundColor: [this.PURPLE_LIGHT, this.ORANGE_LIGHT],
                    borderColor: [this.PURPLE, this.ORANGE],
                    borderWidth: 2
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '55%',
                plugins: {
                    legend: {
                        position: 'bottom',
                        labels: {
                            usePointStyle: true,
                            padding: 16,
                            font: { size: 12, family: "'Segoe UI', 'Raleway'" }
                        }
                    }
                }
            }
        });
    }

    exportFullReport(): void {
        this.isExporting = true;
        const estimateId = this.resolveEstimateId();

        forkJoin({
            estimate: this.estimativaService.getEstimateById(estimateId),
            imputation: this.estimativaService.getImputation(estimateId)
        }).pipe(
            switchMap(({ estimate, imputation }: { estimate: any, imputation: any }) => {
                const reportData = {
                    estimate: estimate,
                    imputation: imputation,
                    deviations: this.deviationRows,
                    summary: {
                        totalEstimated: this.totalEstimated,
                        totalActual: this.totalActual,
                        totalDeviation: this.totalDeviation,
                        totalDeviationPercent: this.totalDeviationPercent
                    }
                };
                return this.estimativaService.exportFullReport(reportData);
            }),
            finalize(() => this.isExporting = false)
        ).subscribe({
            next: (blob: any) => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `informe_completo_${estimateId}.xlsx`;
                link.click();
                window.URL.revokeObjectURL(url);
            },
            error: (err: any) => {
                console.error('Error exporting full report:', err);
            }
        });
    }
}
